import { ChartColumn, ChevronRight, Mars, UserRound, Venus } from 'lucide-react';
import Screen from '../../demo/Screen';
import {
  AgentsGlyph,
  ChatPhoneGlyph,
  DartGlyph,
  EnvelopeGlyph,
  GroupGlyph,
  PersonListGlyph,
  RiseGlyph,
} from '../../mocks/ProductGlyphs';
import { ProductChip } from './ProductDetail';
import AnalyticsDashboard, { FINDINGS, PROFILES } from './AnalyticsDashboard';
import './Analytics.css';

const ALL = PROFILES[0];

/* Dot colours for the opportunity list; each row also carries its name and share. */
const FINDING_DOTS = {
  vitd: '#e5484d',
  iron: '#f5841f',
  prediabetes: '#e0731a',
  thyroid: '#13a88a',
  ldl: '#f2c94c',
};

const COHORTS = [
  { key: 'women', icon: Venus, title: 'Women 18–45', sub: 'Vitamin D + Iron', count: '1,240' },
  { key: 'men', icon: Mars, title: 'Men 35+', sub: 'Prediabetes risk', count: '980' },
  { key: 'families', icon: UserRound, title: 'Families', sub: 'General health check', count: '860' },
];

const CHANNELS = [
  { key: 'whatsapp', icon: ChatPhoneGlyph, tone: 'green', title: 'WhatsApp', sub: 'Send targeted campaigns' },
  { key: 'email', icon: EnvelopeGlyph, tone: 'blue', title: 'Email', sub: 'Share personalized notes' },
  { key: 'agents', icon: AgentsGlyph, tone: 'purple', title: 'Agents', sub: 'Enable your support team' },
];

function Question({ id, icon: Icon, tone, title, text, children }) {
  return (
    <section className="card an-q" aria-labelledby={id}>
      <span className={`an-q__icon an-q__icon--${tone}`} aria-hidden="true">
        <Icon size={34} />
      </span>
      <h2 id={id} className="an-q__title">
        {title}
      </h2>
      <p className="an-q__text">{text}</p>
      <div className="an-q__body">{children}</div>
    </section>
  );
}

export default function Analytics() {
  return (
    <Screen id="product.analytics" title="Analytics" className="an">
      <div className="pd-hero an-hero">
        <div className="pd-intro">
          <ProductChip icon={ChartColumn} name="Analytics" stage="Growth" tone="growth" />
          <h1 className="headline pd-headline an-headline">
            Turn health data <br />
            into <em>better decisions.</em>
          </h1>
          <p className="lede pd-lede">
            Understand your patients, find opportunities, and act on them — all in one place.
          </p>
        </div>
        <AnalyticsDashboard />
      </div>

      <div className="an-questions">
        <Question
          id="an-q-who"
          icon={GroupGlyph}
          tone="blue"
          title="Who are your patients?"
          text="Age, sex, location, profiles and key health needs."
        >
          <div className="an-profiles">
            <h3 className="an-profiles__title">Patients by profile</h3>
            <ul className="an-hbars an-hbars--soft">
              {PROFILES.slice(1).map((p) => (
                <li key={p.key}>
                  <span className="an-hbars__name">{p.label}</span>
                  <span className="an-hbars__track" aria-hidden="true">
                    <span style={{ width: `${p.share * 2}%` }} />
                  </span>
                  <span className="an-hbars__pct">{p.share}%</span>
                </li>
              ))}
            </ul>
          </div>
        </Question>

        <Question
          id="an-q-top"
          icon={RiseGlyph}
          tone="green"
          title="What are the top opportunities?"
          text="Find the most common findings in your population."
        >
          <ul className="an-dots">
            {FINDINGS.map((f) => (
              <li key={f.key}>
                <span className="an-dots__dot" style={{ background: FINDING_DOTS[f.key] }} aria-hidden="true" />
                <span className="an-dots__name">{f.name}</span>
                <span className="an-dots__pct">{ALL.findings[f.key]}%</span>
              </li>
            ))}
          </ul>
        </Question>

        <Question
          id="an-q-target"
          icon={PersonListGlyph}
          tone="purple"
          title="Who to target next?"
          text="Create cohorts for focused outreach and campaigns."
        >
          <ul className="an-rows">
            {COHORTS.map(({ key, icon: Icon, title, sub, count }) => (
              <li key={key}>
                <span className="an-rows__icon an-rows__icon--purple" aria-hidden="true">
                  <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
                </span>
                <span className="an-rows__text">
                  <strong>{title}</strong>
                  <span>{sub}</span>
                </span>
                <span className="an-rows__count">
                  {count}
                  <span className="sr-only"> patients</span>
                </span>
              </li>
            ))}
          </ul>
        </Question>

        <Question
          id="an-q-act"
          icon={DartGlyph}
          tone="amber"
          title="How to act on these insights?"
          text="Use the segments directly in WhatsApp, email or through agents."
        >
          <ul className="an-rows">
            {CHANNELS.map(({ key, icon: Icon, tone, title, sub }) => (
              <li key={key}>
                <span className={`an-rows__icon an-rows__icon--${tone} an-rows__icon--solid`} aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className="an-rows__text">
                  <strong>{title}</strong>
                  <span>{sub}</span>
                </span>
                <ChevronRight className="an-rows__chev" size={18} strokeWidth={2.2} aria-hidden="true" />
              </li>
            ))}
          </ul>
        </Question>
      </div>

    </Screen>
  );
}
